import type { Chat, ChatMessage, Content } from "@rhema/client";
import type { Format, MentionName } from "@rhema/format";
import {
  CameraIcon,
  type ChannelHit,
  ClockIcon,
  Composer,
  type ComposerProps,
  type ComposerValue,
  cn,
  EditIcon,
  EmojiPicker,
  focusRing,
  MicIcon,
  type PersonHit,
  PlusIcon,
  ProductMark,
  RecordingBar,
  ReplyIcon,
  type RichComposerHandle,
  RichComposerLazy,
  SmileIcon,
  XIcon,
} from "@rhema/ui";
import {
  forwardRef,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router";
import { errorText } from "../billing/UpgradeLink";
import { TimeSheet } from "../later/TimeSheet";
import { sendChoices, whenLabel } from "../later/when";
import { useClient, useClientState, useSession } from "../session/session";
import { channelsOf, homeSpaceId } from "../spaces/data";
import { messageBody } from "./draft";
import { useDrafts } from "./drafts";
import { nameIn, textOf } from "./MessageList";
import { isEditable, mentionsIn, wordsOf } from "./messageMenu";
import { formatOf, mentionIdsOf, mentionNames } from "./rich";

// The message box of a chat, a channel and a thread: the rich editor with its toolbar, the reply
// and edit bars, the actions along the bottom, the draft kept for each chat, and sending later.
// While the editor loads, or if it cannot load, the plain box stands in and still sends.

export type MessageBoxHandle = {
  /** Puts text in the box, such as a reply Queravel drafted. */
  fill: (text: string) => void;
};

type MessageBoxProps = {
  chat: Chat;
  /** Where the draft is kept: the chat id, or threadKey() for a thread. */
  draftKey: string;
  /** Replies go into this thread. */
  threadRoot?: string;
  desktop: boolean;
  placeholder: string;
  /** The messages on screen, for Up arrow and for the reply a draft was keeping. */
  messages: ChatMessage[];
  replyTo: ChatMessage | null;
  onReplyTo: (m: ChatMessage | null) => void;
  editing: ChatMessage | null;
  onEditing: (m: ChatMessage | null) => void;
  onAttach: () => void;
  /** Files pasted or dropped into the box, for the media tray. */
  onFiles?: (files: File[]) => void;
  onAskQueravel?: () => void;
  /** Whether Schedule is offered. */
  schedulable: boolean;
  /** Voice recording and the camera, from useChatMedia. */
  media: Partial<ComposerProps>;
  hint?: string;
  /** Called once a message is on its way, so the list can scroll to it. */
  onSent?: () => void;
  /** Tells the chat whether you are typing. */
  onTyping?: (typing: boolean) => void;
  toast: (message: string) => void;
};

const EMPTY: ComposerValue = { body: "", format: undefined, mentions: [] };

/** The keys that open Queravel: ⌘J on Apple computers, Ctrl+J elsewhere. */
function askKeys(): string {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘J" : "Ctrl+J";
}

/** The line of keys under the box on computers. */
export function keyHint(askable: boolean): string {
  return askable
    ? `Enter to send · Shift and Enter for a new line · ${askKeys()} asks Queravel`
    : "Enter to send · Shift and Enter for a new line";
}

function ActionButton({
  label,
  text,
  onClick,
  children,
  disabled,
  pressed,
  ...rest
}: {
  label: string;
  /** Shown beside the icon where there is room. */
  text?: string;
  onClick?: () => void;
  children: ReactNode;
  disabled?: boolean;
  pressed?: boolean;
  onPointerDown?: ComposerProps["onVoicePress"];
  buttonRef?: Ref<HTMLButtonElement>;
}) {
  const { onPointerDown, buttonRef } = rest;
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      disabled={disabled}
      // Keep the caret where it is in the text.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      onPointerDown={onPointerDown}
      className={cn(
        "flex h-[34px] min-w-[34px] flex-none cursor-pointer touch-none items-center justify-center gap-1.5 rounded-lg px-2 text-[13px] text-ink-secondary hover:bg-surface-header",
        "disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent",
        pressed && "bg-surface-header text-scarlet",
        focusRing,
      )}
    >
      {children}
      {text && <span className="hidden @min-[600px]:inline">{text}</span>}
    </button>
  );
}

function Banner({
  icon,
  title,
  text,
  cancelLabel,
  onCancel,
}: {
  icon: ReactNode;
  title: string;
  text: string;
  cancelLabel: string;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center gap-2.5 rounded-field bg-surface-header py-2 pr-2 pl-3.5">
      {icon}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[12.5px] font-bold text-blue">{title}</span>
        <span className="truncate text-[13.5px] text-ink-secondary">{text}</span>
      </span>
      <button
        type="button"
        aria-label={cancelLabel}
        onClick={onCancel}
        className={cn(
          "flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-full text-ink-secondary hover:bg-surface",
          focusRing,
        )}
      >
        <XIcon size={15} strokeWidth={2.4} />
      </button>
    </div>
  );
}

export const MessageBox = forwardRef<MessageBoxHandle, MessageBoxProps>(function MessageBox(
  {
    chat,
    draftKey,
    threadRoot,
    desktop,
    placeholder,
    messages,
    replyTo,
    onReplyTo,
    editing,
    onEditing,
    onAttach,
    onFiles,
    onAskQueravel,
    schedulable,
    media,
    hint,
    onSent,
    onTyping,
    toast,
  },
  ref,
) {
  const client = useClient();
  const state = useClientState();
  const { platform } = useSession();
  const { spaceId: routeSpace } = useParams();
  const drafts = useDrafts();
  const rich = useRef<RichComposerHandle>(null);
  const meId = state.me?.id;
  const chatId = chat.id;
  const stored = drafts.get(draftKey);

  // What is in the box now, kept as it changes, for drafts and for sending later.
  const value = useRef<ComposerValue>(
    stored ? { body: stored.body, format: stored.format, mentions: stored.mentions ?? [] } : EMPTY,
  );
  const [hasText, setHasText] = useState(Boolean(stored?.body.trim()));
  // The plain box, while the editor loads or if it cannot.
  const [plain, setPlain] = useState(stored?.body ?? "");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const emojiButton = useRef<HTMLButtonElement>(null);
  const emojiPanel = useRef<HTMLDivElement>(null);

  const latest = useRef({ editing, replyTo });
  latest.current = { editing, replyTo };

  const members = chat.members.filter((m) => m.user.id !== meId);
  const names = (ids: readonly string[] | undefined): MentionName[] => mentionNames(chat, ids);

  const saveDraft = useCallback(
    (v: ComposerValue, reply: ChatMessage | null) => {
      drafts.set(draftKey, {
        body: v.body,
        ...(v.format ? { format: v.format } : {}),
        ...(v.mentions.length ? { mentions: v.mentions } : {}),
        ...(reply ? { replyTo: reply.id } : {}),
      });
    },
    [drafts, draftKey],
  );

  // A draft saved before the store finished loading from the device goes in once it has.
  const filled = useRef(Boolean(stored));
  useEffect(() => {
    if (filled.current || !drafts.isLoaded) return;
    filled.current = true;
    const d = drafts.get(draftKey);
    if (!d || latest.current.editing || value.current.body.trim()) return;
    value.current = { body: d.body, format: d.format, mentions: d.mentions ?? [] };
    setHasText(Boolean(d.body.trim()));
    setPlain(d.body);
    rich.current?.setContent(d.body, { format: d.format, mentions: names(d.mentions) });
  });

  // The reply a draft was keeping comes back with it, once its message is on screen.
  const replyRestored = useRef(false);
  useEffect(() => {
    if (replyRestored.current || !drafts.isLoaded) return;
    const id = drafts.get(draftKey)?.replyTo;
    if (!id) {
      replyRestored.current = true;
      return;
    }
    const m = messages.find((x) => x.id === id);
    if (!m) return;
    replyRestored.current = true;
    if (!latest.current.replyTo && !latest.current.editing) onReplyTo(m);
  });

  // Choosing or dropping a reply is part of the draft too.
  const shownReply = useRef(replyTo?.id ?? null);
  useEffect(() => {
    const id = replyTo?.id ?? null;
    if (id === shownReply.current) return;
    shownReply.current = id;
    if (!editing && drafts.isLoaded) saveDraft(value.current, replyTo);
  }, [replyTo, editing, drafts, saveDraft]);

  // Editing puts the message in the box with its formatting and mentions. What was being written
  // waits, and comes back when the edit is saved or cancelled.
  const waiting = useRef<ComposerValue | null>(null);
  const editingId = editing?.id ?? null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when an edit starts or ends
  useLayoutEffect(() => {
    if (editing) {
      if (!waiting.current) waiting.current = value.current;
      const body = wordsOf(editing);
      const format = formatOf(editing);
      value.current = { body, format, mentions: mentionIdsOf(editing) };
      setHasText(Boolean(body.trim()));
      setPlain(body);
      rich.current?.setContent(body, { format, mentions: names(mentionIdsOf(editing)) });
      return;
    }
    const back = waiting.current;
    if (!back) return;
    waiting.current = null;
    value.current = back;
    setHasText(Boolean(back.body.trim()));
    setPlain(back.body);
    rich.current?.setContent(back.body, { format: back.format, mentions: names(back.mentions) });
  }, [editingId]);

  const onChange = (v: ComposerValue) => {
    value.current = v;
    setHasText(Boolean(v.body.trim()));
    // The words of a message being edited are not a draft.
    if (latest.current.editing) return;
    saveDraft(v, latest.current.replyTo);
    onTyping?.(v.body.trim().length > 0);
  };

  useImperativeHandle(ref, () => ({
    fill: (text: string) => {
      onEditing(null);
      waiting.current = null;
      const v: ComposerValue = { body: text, format: undefined, mentions: [] };
      value.current = v;
      setHasText(Boolean(text.trim()));
      setPlain(text);
      rich.current?.setContent(text);
      saveDraft(v, latest.current.replyTo);
    },
  }));

  const send = (v: ComposerValue): boolean => {
    if (!v.body.trim()) return false;
    const target = latest.current.editing;
    if (target) {
      void client
        .edit(chatId, target.id, v.body, v.format, v.mentions.length ? v.mentions : undefined)
        .catch(() => toast("That edit did not go through."));
      onEditing(null);
      onSent?.();
      return true;
    }
    const reply = latest.current.replyTo;
    const content: Content = {
      type: "text",
      body: v.body,
      ...(v.format ? { format: v.format } : {}),
      ...(reply ? { reply_to: reply.id } : {}),
      ...(v.mentions.length ? { mentions: v.mentions } : {}),
    };
    void client
      .send(chatId, content, {
        ...(threadRoot ? { threadRoot } : {}),
        ...(v.mentions.length ? { mentions: v.mentions } : {}),
      })
      .catch(() => toast("That message could not be saved on this device."));
    value.current = EMPTY;
    setHasText(false);
    drafts.clear(draftKey);
    onTyping?.(false);
    onReplyTo(null);
    onSent?.();
    return true;
  };

  const scheduleAt = (at: Date) => {
    setScheduling(false);
    const v = value.current;
    if (!v.body.trim()) return;
    const reply = latest.current.replyTo;
    const content: Content = {
      type: "text",
      body: v.body,
      ...(v.format ? { format: v.format } : {}),
      ...(reply ? { reply_to: reply.id } : {}),
      ...(v.mentions.length ? { mentions: v.mentions } : {}),
    };
    onTyping?.(false);
    void client
      .schedule(chatId, content, at, {
        ...(threadRoot ? { threadRoot } : {}),
        ...(v.mentions.length ? { mentions: v.mentions } : {}),
      })
      .then(() => toast(`Scheduled for ${whenLabel(at).toLowerCase()}.`))
      .catch((e: Error) => toast(errorText(e, platform)));
    value.current = EMPTY;
    setHasText(false);
    setPlain("");
    rich.current?.clear();
    drafts.clear(draftKey);
    onReplyTo(null);
  };

  const cancel = (): boolean => {
    if (latest.current.editing) {
      onEditing(null);
      return true;
    }
    if (latest.current.replyTo) {
      onReplyTo(null);
      return true;
    }
    return false;
  };

  const editLast = (): boolean => {
    if (latest.current.editing) return false;
    const last = [...messages].reverse().find((m) => isEditable(m, meId));
    if (!last) return false;
    onEditing(last);
    return true;
  };

  // People who can be mentioned here: the members of a group or channel, or the other person in a
  // direct chat. Names that start with what was typed come first.
  const searchPeople = (query: string): PersonHit[] => {
    const q = query.toLowerCase();
    return members
      .map((m) => m.user)
      .filter((u) => !q || u.display_name.toLowerCase().includes(q) || u.handle?.toLowerCase().includes(q))
      .sort((a, b) => {
        const sa = a.display_name.toLowerCase().startsWith(q) ? 0 : 1;
        const sb = b.display_name.toLowerCase().startsWith(q) ? 0 : 1;
        return sa - sb || a.display_name.localeCompare(b.display_name);
      })
      .slice(0, 8)
      .map((u) => ({ id: u.id, name: u.display_name, ...(u.handle ? { detail: `@${u.handle}` } : {}) }));
  };

  // Channels you can open: those of the space on screen, or of every space from a chat.
  const spaceId = routeSpace || (chat.kind === "channel" ? homeSpaceId(state, chat) : undefined);
  const searchChannels = (query: string): ChannelHit[] => {
    const q = query.toLowerCase();
    const list = spaceId
      ? channelsOf(state, spaceId)
      : Object.values(state.chats).filter((c) => c.kind === "channel");
    return list
      .filter((c) => c.title.toLowerCase().includes(q))
      .sort((a, b) => {
        const sa = a.title.toLowerCase().startsWith(q) ? 0 : 1;
        const sb = b.title.toLowerCase().startsWith(q) ? 0 : 1;
        return sa - sb || a.title.localeCompare(b.title);
      })
      .slice(0, 8)
      .map((c) => ({
        id: c.id,
        name: c.title,
        ...(!spaceId && c.space_name ? { detail: c.space_name } : {}),
      }));
  };

  // A click anywhere else closes the emoji picker.
  useEffect(() => {
    if (!emojiOpen) return;
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (emojiPanel.current?.contains(t) || emojiButton.current?.contains(t)) return;
      setEmojiOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [emojiOpen]);

  const recording = media.recording;
  // A pointer press records through onVoicePress. The click after it is ignored, so only a
  // keyboard press reaches onVoice.
  const voiceClick = (e?: { detail: number }) => {
    if (!media.onVoicePress || e?.detail === 0) media.onVoice?.();
  };

  const banner = recording ? (
    <div className="flex min-h-[46px] items-center rounded-field bg-surface-header">
      <RecordingBar recording={recording} />
    </div>
  ) : editing ? (
    <Banner
      icon={<EditIcon size={17} strokeWidth={2.2} className="flex-none text-blue" />}
      title={
        editing.content?.type === "media"
          ? "Editing the caption"
          : threadRoot
            ? "Editing your reply"
            : "Editing your message"
      }
      text={textOf(editing)}
      cancelLabel="Cancel editing"
      onCancel={() => onEditing(null)}
    />
  ) : replyTo ? (
    <Banner
      icon={<ReplyIcon size={17} strokeWidth={2.2} className="flex-none text-blue" />}
      title={`Replying to ${nameIn(chat, replyTo.senderId, meId)}`}
      text={textOf(replyTo)}
      cancelLabel="Cancel reply"
      onCancel={() => onReplyTo(null)}
    />
  ) : null;

  const canSchedule = schedulable && !editing;
  const actions = (
    <>
      <ActionButton label="Attach" text="Attach" onClick={onAttach}>
        <PlusIcon size={18} strokeWidth={2.2} />
      </ActionButton>
      {!desktop && media.onCamera && (
        <ActionButton label="Camera" onClick={media.onCamera}>
          <CameraIcon size={18} />
        </ActionButton>
      )}
      <ActionButton
        label="Emoji"
        text="Emoji"
        buttonRef={emojiButton}
        pressed={emojiOpen ? true : undefined}
        onClick={() => setEmojiOpen((o) => !o)}
      >
        <SmileIcon size={18} />
      </ActionButton>
      <ActionButton label="Mention someone" text="Mention" onClick={() => rich.current?.startMention()}>
        <span aria-hidden="true" className="text-[16px] leading-none font-semibold">
          @
        </span>
      </ActionButton>
      <ActionButton
        label={media.onVoicePress ? "Hold to record a voice message" : "Record voice message"}
        text="Voice"
        pressed={recording ? true : undefined}
        onClick={() => voiceClick({ detail: 0 })}
        onPointerDown={media.onVoicePress}
      >
        <MicIcon size={18} strokeWidth={2.2} />
      </ActionButton>
      {canSchedule && (desktop || hasText) && (
        <ActionButton
          label="Schedule message"
          text="Schedule"
          disabled={!hasText}
          onClick={() => setScheduling(true)}
        >
          <ClockIcon size={18} strokeWidth={2.2} />
        </ActionButton>
      )}
      {/* Computers ask Queravel from the top bar and with the keys, as the hint says. */}
      {!desktop && onAskQueravel && (
        <button
          type="button"
          aria-label="Ask Queravel"
          title="Ask Queravel"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onAskQueravel}
          className={cn(
            "flex h-[34px] w-[34px] flex-none cursor-pointer items-center justify-center rounded-lg",
            focusRing,
          )}
        >
          <ProductMark product="queravel" size={26} />
        </button>
      )}
    </>
  );

  // The plain box, until the editor has loaded or if it cannot load. It sends plain text, with the
  // people named in it, and keeps Up arrow to edit and Escape to stop.
  const fallbackKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).tagName !== "TEXTAREA") return;
    if (e.key === "Escape" && cancel()) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (e.key !== "ArrowUp" || plain !== "" || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey) return;
    if (editLast()) e.preventDefault();
  };
  const fallback = (
    // biome-ignore lint/a11y/noStaticElementInteractions: Up arrow and Escape in the message box
    <div className="contents" onKeyDown={fallbackKeys}>
      {editing && (
        <div className={cn("flex-none border-t border-line bg-linen pt-2", desktop ? "px-5" : "px-3")}>
          {banner}
        </div>
      )}
      <Composer
        variant={desktop ? "desktop" : "mobile"}
        value={plain}
        onChange={(text) => {
          setPlain(text);
          const body = messageBody(text);
          onChange({ body, format: undefined, mentions: mentionsIn(body, members) });
        }}
        onSend={() => {
          const body = messageBody(plain);
          if (send({ body, format: undefined, mentions: mentionsIn(body, members) })) setPlain("");
        }}
        onAttach={onAttach}
        onAskQueravel={onAskQueravel}
        {...(canSchedule ? { onSchedule: () => setScheduling(true) } : {})}
        {...media}
        placeholder={placeholder}
        replyTo={
          replyTo ? { author: nameIn(chat, replyTo.senderId, meId), text: textOf(replyTo) } : undefined
        }
        onCancelReply={() => onReplyTo(null)}
        hint={hint}
      />
    </div>
  );

  const format: Format = value.current.format;
  return (
    <>
      <RichComposerLazy
        // A new chat or thread starts a new editor, holding that chat's draft.
        key={draftKey}
        ref={rich}
        fallback={fallback}
        mode={desktop ? "desktop" : "phone"}
        initialBody={value.current.body}
        initialFormat={format}
        initialMentions={names(value.current.mentions)}
        placeholder={placeholder}
        label="Message"
        // An edit started while the editor was loading goes on with the caret at its end.
        autoFocus={Boolean(editing)}
        onChange={onChange}
        onSend={send}
        searchPeople={searchPeople}
        searchChannels={searchChannels}
        onPasteFiles={onFiles}
        onEscape={cancel}
        onEditLast={editLast}
        banner={banner}
        sendLabel={editing ? "Save" : "Send"}
        actions={actions}
        className={cn(
          "@container relative flex-none border-t border-line bg-linen",
          desktop ? "px-5 pt-3 pb-3" : "px-3 pt-2 pb-[max(12px,env(safe-area-inset-bottom))]",
        )}
        footer={
          <>
            {desktop && hint && <p className="m-0 text-xs text-ink-meta">{hint}</p>}
            {emojiOpen && (
              <div
                ref={emojiPanel}
                role="dialog"
                aria-label="Emoji"
                className={cn("absolute bottom-full z-30 mb-2", desktop ? "right-5" : "right-3 left-3")}
              >
                <EmojiPicker
                  label="Add an emoji"
                  onPick={(emoji, keepOpen) => {
                    rich.current?.insertText(emoji);
                    if (!keepOpen) setEmojiOpen(false);
                  }}
                  onClose={() => {
                    setEmojiOpen(false);
                    rich.current?.focus();
                  }}
                />
              </div>
            )}
          </>
        }
      />
      <TimeSheet
        open={scheduling}
        title="Send later"
        subtitle="Your message is encrypted now and sent at the time you pick."
        choices={sendChoices()}
        pickLabel="Schedule"
        onPick={scheduleAt}
        onClose={() => setScheduling(false)}
      />
    </>
  );
});
